import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { providerService } from "@/services/providerService";
import { fmtDate } from "@/lib/format";

const AGE_LABEL = (h) => {
  if (h === null || h === undefined) return "nunca atualizado";
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
};

export const StalePricesCard = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await providerService.staleAssets());
    } catch (e) {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const items = report?.items || [];
  const clean = report && items.length === 0;

  return (
    <section
      data-testid="stale-prices-card"
      className={`rounded-md border bg-[#12141A] p-6 sm:p-8 ${
        items.length ? "border-[#F59E0B]/50" : "border-[#22252F]"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className={`h-4 w-4 ${items.length ? "text-[#F59E0B]" : "text-[#10B981]"}`} />
          <h2 className="font-heading text-lg font-light tracking-tight text-white md:text-lg">
            Alerta de preços parados
          </h2>
          <span
            data-testid="stale-prices-count"
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]"
          >
            {items.length} ativo(s) · limite {report?.threshold_hours ?? 48}h
          </span>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="stale-prices-reload"
          className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white disabled:opacity-40"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Verificar
        </button>
      </div>

      {clean ? (
        <p data-testid="stale-prices-empty" className="mt-5 text-sm text-[#94A3B8]">
          Todos os ativos dos mercados abertos foram atualizados nas últimas {report.threshold_hours}h.
          {report.markets_closed?.length
            ? ` Ignorados (mercado fechado): ${report.markets_closed.join(", ")}.`
            : ""}
        </p>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="py-3 pr-6">Ativo</th>
                <th className="py-3 pr-6">Mercado</th>
                <th className="py-3 pr-6">Sem atualizar</th>
                <th className="py-3 pr-6">Última atualização</th>
                <th className="py-3">Provedor</th>
              </tr>
            </thead>
            <tbody data-testid="stale-prices-body">
              {items.map((i) => (
                <tr key={`${i.market}-${i.symbol}`} data-testid={`stale-row-${i.symbol}`} className="border-t border-[#1A1D25]">
                  <td className="py-3 pr-6 font-mono text-white">{i.symbol}</td>
                  <td className="py-3 pr-6 font-mono text-xs text-[#94A3B8]">{i.market}</td>
                  <td
                    className={`py-3 pr-6 font-mono text-xs ${
                      i.severity === "critical" ? "text-[#EF4444]" : "text-[#F59E0B]"
                    }`}
                  >
                    {AGE_LABEL(i.age_hours)}
                  </td>
                  <td className="py-3 pr-6 font-mono text-xs text-[#94A3B8]">
                    {i.last_price_at ? fmtDate(i.last_price_at) : "—"}
                  </td>
                  <td className="py-3 font-mono text-xs text-[#94A3B8]">{i.source || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {report?.markets_closed?.length ? (
            <p className="mt-4 text-xs text-[#64748B]">
              Mercados fechados neste momento (ignorados): {report.markets_closed.join(", ")}.
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
};
